import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import http from '../components/services/HttpService';
import { AUTO_LOCK } from '../../src/components/store/actions';

interface ContextInterface {
  elapsed: number;
  remaining: number;
  maxTime: number;
  onLoginSuccess(): void;
}

interface PropsInterface {
  children: any;
}

const AutoLockContext = createContext<ContextInterface>({
  elapsed: 0,
  remaining: 0,
  maxTime: 0,
  onLoginSuccess() {},
});

export const AutoLockConsumer = AutoLockContext.Consumer;

const maxTime = 180 * 60; // 3 hours

export function AutoLockProvider(props: PropsInterface) {
  const dispatch = useDispatch();

  const [remaining, setRemaining] = useState(maxTime);
  const refTimer = useRef<null | NodeJS.Timeout>(null);
  const refTimedOut = useRef<boolean>(false);

  const stopTimer = useCallback(() => {
    clearInterval(refTimer.current);
    refTimer.current = null;
  }, []);

  const resetTimer = useCallback(() => {
    stopTimer();
    setRemaining(maxTime);

    refTimer.current = setInterval(() => {
      onTimeTick();
    }, 1000);
  }, []);

  const onLoginSuccess = useCallback(() => {
    refTimedOut.current = false;
    resetTimer();
  }, []);

  const onTimeTick = useCallback(() => {
    if (refTimedOut.current) {
      return;
    }

    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return;
    }

    setRemaining((oldValue: number) => {
      if (oldValue <= 0) {
        http.post('/auth/auto-lock').finally(() => {
          dispatch({
            type: AUTO_LOCK,
          });

          refTimedOut.current = true;
          stopTimer();

          return maxTime;
        });
      }

      return oldValue - 1;
    });
  }, []);

  useEffect(() => {
    resetTimer();
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('mousedown', resetTimer);

    return () => {
      stopTimer();
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('mousedown', resetTimer);
    };
  }, []);

  return (
    <AutoLockContext.Provider
      value={{
        elapsed: maxTime - remaining,
        remaining,
        maxTime,
        onLoginSuccess,
      }}
    >
      {props.children}
    </AutoLockContext.Provider>
  );
}

export function useAutoLock() {
  const context = useContext(AutoLockContext);
  if (!context) {
    throw new Error('useAutoLock hook can called only in AutoLockProvider');
  }

  return context;
}
